import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Plus, RefreshCw, Trash2, TrendingUp, X } from "lucide-react";
import { toast } from "sonner";
import {
  addHolding,
  deleteHolding,
  getInvestCoach,
  getPortfolio,
  getPortfolioHistory,
  getPortfolioSignals,
  portfolioReset,
  updateHolding,
  updateRiskProfile,
} from "@/lib/api";
import { AddAssetForm } from "./investments/AddAssetForm";
import { CoachPanel } from "./investments/CoachPanel";
import { HoldingsList } from "./investments/HoldingsList";
import { IdeasPanel } from "./investments/IdeasPanel";
import { PortfolioOverview } from "./investments/PortfolioOverview";
import { SignalsPanel } from "./investments/SignalsPanel";
import { TEAL } from "./investments/shared";

export const InvestmentsSheet = ({ open, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [coach, setCoach] = useState(null);
  const [riskBusy, setRiskBusy] = useState(false);
  const [view, setView] = useState("list"); // list | add
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ quantity: "", avg_price: "", avg_price_currency: "EUR", broker: "", manual_price: "" });
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null);
  const [signals, setSignals] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false); // azzera portafoglio (2 passi)
  const [resetBusy, setResetBusy] = useState(false);

  const load = useCallback(async () => {
    setData(null);
    setHistory(null);
    setSignals(null);
    try {
      const d = await getPortfolio();
      setData(d);
      if ((d?.holdings || []).length > 0) {
        getPortfolioHistory(90).then((h) => setHistory(h.snapshots || [])).catch(() => setHistory([]));
        getPortfolioSignals().then((s) => setSignals(s)).catch(() => setSignals(null));
      } else {
        setHistory([]);
      }
    } catch {
      toast.error("Impossibile caricare il portafoglio");
    }
  }, []);

  const loadCoach = useCallback(async () => {
    try {
      setCoach(await getInvestCoach());
    } catch (e) {
      // Il coach è informativo: non blocca lo sheet, ma logghiamo per il debug.
      console.warn("[Liqo] getInvestCoach fallito:", e);
    }
  }, []);

  const changeRisk = async (rid) => {
    if (riskBusy || coach?.risk_profile === rid) return;
    setRiskBusy(true);
    const prev = coach;
    setCoach((c) => (c ? { ...c, risk_profile: rid } : c)); // ottimistico
    try {
      const res = await updateRiskProfile(rid);
      setCoach(res.coach);
      toast.success(`Profilo di rischio: ${res.coach.risk_label}`);
      onChanged?.(); // aggiorna il feed Home (nuova card INVEST_TIP)
    } catch {
      setCoach(prev);
      toast.error("Impossibile aggiornare il profilo di rischio");
    } finally {
      setRiskBusy(false);
    }
  };

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      setView("list");
      setEditingId(null);
      setConfirmReset(false);
      setCoach(null);
      load();
      loadCoach();
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, load, loadCoach]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const refreshAfter = (resp) => {
    setData(resp);
    onChanged?.();
    if ((resp?.holdings || []).length > 0) {
      getPortfolioHistory(90).then((h) => setHistory(h.snapshots || [])).catch(() => {});
      getPortfolioSignals().then((s) => setSignals(s)).catch(() => {});
    } else {
      setHistory([]);
      setSignals(null);
    }
  };

  const handleAdd = async (payload, symbol) => {
    setBusy(true);
    try {
      const resp = await addHolding(payload);
      refreshAfter(resp);
      toast.success(`${symbol} aggiunto al portafoglio`);
      setView("list");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile aggiungere l'asset");
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (h) => {
    setEditingId(h.id);
    setEditForm({
      quantity: String(h.quantity),
      avg_price: String(h.avg_price),
      avg_price_currency: (h.avg_price_currency || "EUR").toUpperCase(),
      broker: h.broker || "",
      manual_price: h.is_manual && h.manual_price != null ? String(h.manual_price) : "",
    });
  };

  const saveEdit = async (id) => {
    const q = parseFloat(String(editForm.quantity).replace(",", "."));
    const a = parseFloat(String(editForm.avg_price).replace(",", "."));
    if (!q || q <= 0) return toast.error("Quantità non valida");
    if (Number.isNaN(a) || a < 0) return toast.error("Prezzo medio non valido");
    // prezzo manuale: vuoto -> 0 (rimuove/torna a live), valore -> imposta
    const manualStr = String(editForm.manual_price).trim();
    let manual = 0;
    if (manualStr) {
      manual = parseFloat(manualStr.replace(",", "."));
      if (Number.isNaN(manual) || manual < 0) return toast.error("Prezzo manuale non valido");
    }
    setBusy(true);
    try {
      const resp = await updateHolding(id, {
        quantity: q,
        avg_price: a,
        avg_price_currency: editForm.avg_price_currency || "EUR",
        broker: (editForm.broker || "").trim(),
        manual_price: manual,
      });
      refreshAfter(resp);
      setEditingId(null);
      toast.success("Asset aggiornato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aggiornamento non riuscito");
    } finally {
      setBusy(false);
    }
  };

  const removeHolding = async (id) => {
    setBusy(true);
    try {
      const resp = await deleteHolding(id);
      refreshAfter(resp);
      setEditingId(null);
      toast.success("Asset rimosso");
    } catch {
      toast.error("Rimozione non riuscita");
    } finally {
      setBusy(false);
    }
  };

  const handlePortfolioReset = async () => {
    setResetBusy(true);
    try {
      const resp = await portfolioReset();
      refreshAfter(resp.portfolio || resp);
      setEditingId(null);
      setConfirmReset(false);
      toast.success(`Portafoglio azzerato (${resp.removed ?? 0} asset rimossi)`);
    } catch {
      toast.error("Impossibile azzerare il portafoglio");
    } finally {
      setResetBusy(false);
    }
  };

  const holdings = data?.holdings || [];

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50" data-testid="investments-sheet">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-x-0 bottom-0 top-12 mx-auto flex max-w-[430px] flex-col overflow-hidden rounded-t-[12px] border border-b-0 border-white/[0.08] bg-[#030305]"
          >
            <div className="flex items-center justify-between border-b border-white/[0.06] px-4 pb-3 pt-3">
              <span className="mono flex items-center gap-2 text-sm font-medium tracking-[0.1em]" style={{ color: TEAL }}>
                {view === "add" ? (
                  <button onClick={() => setView("list")} data-testid="add-back-button" aria-label="Indietro">
                    <ArrowLeft size={15} />
                  </button>
                ) : (
                  <TrendingUp size={15} />
                )}
                {view === "add" ? "AGGIUNGI ASSET" : "PATRIMONIO"} <span className="text-white/25">//</span> LIQO
              </span>
              <button
                onClick={onClose}
                data-testid="investments-close-button"
                aria-label="Chiudi"
                className="flex h-8 w-8 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/55 hover:bg-white/[0.05]"
              >
                <X size={14} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              {view !== "add" && <CoachPanel coach={coach} riskBusy={riskBusy} onChangeRisk={changeRisk} />}
              {view !== "add" && data !== null && <IdeasPanel />}
              {/* ---------------- ADD VIEW ---------------- */}
              {view === "add" ? (
                <AddAssetForm busy={busy} onConfirm={handleAdd} />
              ) : data === null ? (
                <div className="space-y-4" data-testid="investments-loading">
                  <div className="h-28 animate-pulse rounded-[8px] border border-white/[0.06] bg-[#0D0D14]" />
                  <div className="h-40 animate-pulse rounded-[8px] border border-white/[0.06] bg-[#0D0D14]" />
                </div>
              ) : holdings.length === 0 ? (
                <div className="flex flex-col items-center pt-6 text-center" data-testid="investments-empty">
                  <span className="mono text-2xl" style={{ color: TEAL }}>↗</span>
                  <p className="mono mt-4 text-sm font-light text-white/85">Nessun investimento</p>
                  <p className="mt-2 max-w-[280px] text-[12px] font-light text-white/40">
                    Aggiungi le tue posizioni (azioni/ETF da Fineco, crypto da exchange) con tag broker e, se serve, prezzo manuale. Calcolo valore e P/L in tempo reale.
                  </p>
                  <button
                    onClick={() => setView("add")}
                    data-testid="investments-empty-add-button"
                    className="mono mt-6 flex h-11 items-center gap-2 rounded-[6px] border px-5 text-[11px] font-medium uppercase tracking-[0.18em] active:scale-[0.98]"
                    style={{ borderColor: `${TEAL}55`, color: TEAL }}
                  >
                    <Plus size={14} /> Aggiungi il primo asset
                  </button>
                </div>
              ) : (
                <>
                  <PortfolioOverview data={data} history={history} />

                  <HoldingsList
                    holdings={holdings}
                    editingId={editingId}
                    editForm={editForm}
                    setEditForm={setEditForm}
                    busy={busy}
                    onAdd={() => setView("add")}
                    onOpenEdit={openEdit}
                    onCloseEdit={() => setEditingId(null)}
                    onSave={saveEdit}
                    onRemove={removeHolding}
                  />

                  <SignalsPanel signals={signals} />

                  {/* ---- Azzera portafoglio (rimozione investimenti demo/di test) ---- */}
                  <div className="mt-6 border-t border-white/[0.06] pt-4">
                    {!confirmReset ? (
                      <button
                        onClick={() => setConfirmReset(true)}
                        data-testid="portfolio-reset-button"
                        className="mono flex h-10 w-full items-center justify-center gap-2 rounded-[6px] border border-[#FF4D4D]/35 bg-transparent text-[10px] font-medium uppercase tracking-[0.16em] text-[#FF4D4D]/90 hover:bg-[#FF4D4D]/[0.06] active:scale-[0.98]"
                      >
                        <Trash2 size={13} />
                        Azzera portafoglio (rimuovi tutti gli asset)
                      </button>
                    ) : (
                      <div className="space-y-2" data-testid="portfolio-reset-confirm">
                        <p className="mono text-center text-[10px] leading-4 tracking-[0.08em] text-white/45">
                          RIMUOVE TUTTI I {holdings.length} ASSET · AZIONE DEFINITIVA
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setConfirmReset(false)}
                            disabled={resetBusy}
                            data-testid="portfolio-reset-cancel"
                            className="mono flex h-11 flex-1 items-center justify-center rounded-[6px] border border-white/[0.1] bg-transparent text-[10px] font-medium uppercase tracking-[0.16em] text-white/50 hover:bg-white/[0.04] active:scale-[0.98] disabled:opacity-50"
                          >
                            Annulla
                          </button>
                          <button
                            onClick={handlePortfolioReset}
                            disabled={resetBusy}
                            data-testid="portfolio-reset-confirm-button"
                            className="mono flex h-11 flex-[1.4] items-center justify-center gap-2 rounded-[6px] border border-[#FF4D4D]/50 bg-[#FF4D4D]/[0.08] text-[10px] font-medium uppercase tracking-[0.14em] text-[#FF4D4D] hover:bg-[#FF4D4D]/[0.14] active:scale-[0.98] disabled:opacity-50"
                          >
                            {resetBusy ? <RefreshCw size={12} className="animate-spin" /> : <Trash2 size={12} />}
                            {resetBusy ? "Azzero…" : "Conferma azzeramento"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <p className="mono mt-5 px-1 text-center text-[10px] leading-4 tracking-[0.05em] text-white/25">
                    Prezzi live: CoinGecko (crypto) · Yahoo Finance / Twelve Data (azioni·ETF) · aggiornamento auto ogni 6h. Imposta un prezzo manuale per gli strumenti non quotati.
                  </p>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
